/**
 * UpdatesNoticesPanel — shared "Updates & Notices" feed used by every portal
 * (Resident app, Tenant dashboard, PM portal, Field Staff app).
 * Tabs: All | Action Required | Service | Procedures | Knowledge | App Updates.
 * Optional `trainingSlot` renders the legacy Training content in a sub-tab.
 */
import { useCallback, useEffect, useState } from 'react';
import { Megaphone, GraduationCap, ChevronLeft, CheckCircle2, BadgeCheck, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../lib/apiFetch';

const GOLD = '#C9A962';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'action', label: 'Action Required' },
  { key: 'service', label: 'Service' },
  { key: 'procedures', label: 'Procedures' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'app', label: 'App Updates' },
];

const TAB_MATCH = {
  all: () => true,
  action: (u) => u.ack_type === 'understand' && !u.read,
  service: (u) => ['service_update', 'service_improvement', 'maintenance_notice'].includes(u.category),
  procedures: (u) => ['procedure_standards', 'policy_update', 'important_notice'].includes(u.category),
  knowledge: (u) => u.category === 'new_knowledge',
  app: (u) => u.category === 'app_platform_update',
};

export function useUpdatesUnread() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const d = await apiFetch('/api/updates/unread-count');
        if (alive) setCount(d.unread_count || 0);
      } catch (_) { /* noop */ }
    };
    fetchCount();
    window.addEventListener('necs-updates-changed', fetchCount);
    return () => { alive = false; window.removeEventListener('necs-updates-changed', fetchCount); };
  }, []);
  return count;
}

export function notifyUpdatesChanged() {
  window.dispatchEvent(new Event('necs-updates-changed'));
}

function isEmbedVideo(url) {
  return /youtube\.com|youtu\.be|vimeo\.com/.test(url || '');
}

function toEmbedUrl(url) {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

export function UpdateBody({ update }) {
  return (
    <div>
      <p className="text-sm text-zinc-300 whitespace-pre-wrap break-words leading-relaxed">{update.body}</p>
      {update.video_url && (
        <div className="mt-3 rounded-xl overflow-hidden border border-zinc-800">
          {isEmbedVideo(update.video_url) ? (
            <iframe title={update.title} src={toEmbedUrl(update.video_url)}
              className="w-full aspect-video" allowFullScreen />
          ) : (
            <video controls className="w-full" src={update.video_url} />
          )}
        </div>
      )}
    </div>
  );
}

export function CategoryChip({ update }) {
  return (
    <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-[9px] font-black uppercase tracking-wider shrink-0"
      style={{ color: GOLD }}>
      {update.category_label || update.category}
    </span>
  );
}

function UpdateDetail({ update, onBack, onAcked, testIdPrefix }) {
  const [busy, setBusy] = useState(false);
  const ack = async () => {
    setBusy(true);
    try {
      await apiFetch(`/api/updates/${update.update_id}/acknowledge`, { method: 'POST' });
      toast.success(update.ack_type === 'understand' ? 'Acknowledged' : 'Marked as read');
      notifyUpdatesChanged();
      onAcked();
    } catch (e) { toast.error(e.detail || 'Failed'); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`${testIdPrefix}-detail`}>
      <button onClick={onBack} className="flex items-center gap-1 text-[11px] font-bold mb-3" style={{ color: GOLD }}
        data-testid={`${testIdPrefix}-back-btn`}>
        <ChevronLeft className="h-3.5 w-3.5" /> Back to Updates
      </button>
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <CategoryChip update={update} />
          <span className="flex items-center gap-1 text-[10px] text-zinc-500">
            <CalendarDays className="h-3 w-3" /> Effective {update.effective_date}
          </span>
        </div>
        <h3 className="text-lg font-black text-white mt-2 break-words"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{update.title}</h3>
        <p className="text-[10px] text-zinc-600 mb-3">{new Date(update.created_at).toLocaleString()}</p>
        <UpdateBody update={update} />
        <div className="mt-5 border-t border-zinc-800 pt-4">
          {update.read ? (
            <p className="flex items-center gap-1.5 text-xs font-bold text-emerald-400" data-testid={`${testIdPrefix}-acked-state`}>
              <BadgeCheck className="h-4 w-4" />
              {update.ack_type === 'understand' ? 'Acknowledged' : 'Read'}
              {update.acked_at && ` · ${new Date(update.acked_at).toLocaleString()}`}
            </p>
          ) : (
            <button onClick={ack} disabled={busy}
              className="w-full rounded-xl py-3 text-sm font-black uppercase tracking-wider"
              style={{ backgroundColor: GOLD, color: '#1a1a1a' }}
              data-testid={`${testIdPrefix}-ack-btn`}>
              {update.ack_type === 'understand' ? 'I Understand' : 'Mark as Read'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function UpdatesNoticesPanel({ trainingSlot = null, testIdPrefix = 'updates' }) {
  const [view, setView] = useState('updates');
  const [tab, setTab] = useState('all');
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/updates');
      setRows(d.updates || []);
      setUnread(d.unread_count || 0);
    } catch (_) { setRows([]); }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener('necs-updates-changed', load);
    return () => window.removeEventListener('necs-updates-changed', load);
  }, [load]);

  const visible = (rows || []).filter(TAB_MATCH[tab] || TAB_MATCH.all);
  const actionCount = (rows || []).filter(TAB_MATCH.action).length;

  return (
    <div className="rounded-2xl border border-zinc-800 p-4" style={{ backgroundColor: '#141414' }} data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" style={{ color: GOLD }} />
          <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            UPDATES &amp; NOTICES
          </h3>
          {unread > 0 && (
            <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid={`${testIdPrefix}-unread-badge`}>{unread}</span>
          )}
        </div>
        {trainingSlot && (
          <div className="flex rounded-lg overflow-hidden border border-zinc-700">
            {[{ k: 'updates', l: 'Updates', I: Megaphone }, { k: 'training', l: 'Training', I: GraduationCap }].map(({ k, l, I }) => (
              <button key={k} onClick={() => { setView(k); setSelected(null); }}
                className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider"
                style={view === k ? { backgroundColor: GOLD, color: '#1a1a1a' } : { color: '#a1a1aa' }}
                data-testid={`${testIdPrefix}-subtab-${k}`}>
                <I className="h-3.5 w-3.5" /> {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === 'training' ? trainingSlot : (
        selected ? (
          <UpdateDetail update={selected} testIdPrefix={testIdPrefix}
            onBack={() => setSelected(null)}
            onAcked={async () => { await load(); setSelected(null); }} />
        ) : (
          <>
            <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className="px-2.5 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider whitespace-nowrap border"
                  style={tab === t.key
                    ? { backgroundColor: 'rgba(201,169,98,0.12)', borderColor: GOLD, color: GOLD }
                    : { borderColor: '#3f3f46', color: '#71717a' }}
                  data-testid={`${testIdPrefix}-tab-${t.key}`}>
                  {t.label}{t.key === 'action' && actionCount > 0 && ` · ${actionCount}`}
                </button>
              ))}
            </div>
            {rows === null && <p className="text-xs text-zinc-600 py-6 text-center">Loading…</p>}
            {rows !== null && visible.length === 0 && (
              <div className="rounded-xl border border-zinc-800 p-8 text-center">
                <Megaphone className="h-6 w-6 mx-auto text-zinc-700 mb-2" />
                <p className="text-xs text-zinc-600">No updates here yet. New company updates and notices will appear in this feed.</p>
              </div>
            )}
            <div className="space-y-2">
              {visible.map((u) => (
                <button key={u.update_id} onClick={() => setSelected(u)}
                  className={`w-full text-left rounded-xl border p-3.5 ${u.read ? 'border-zinc-800 bg-zinc-900/40' : 'border-[#C9A962]/50 bg-[#C9A962]/5'}`}
                  data-testid={`${testIdPrefix}-item-${u.update_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <CategoryChip update={u} />
                      {u.ack_type === 'understand' && !u.read && (
                        <span className="px-1.5 py-0.5 rounded bg-red-950/60 border border-red-900 text-red-400 text-[9px] font-black uppercase tracking-wider shrink-0">Action Required</span>
                      )}
                    </div>
                    {!u.read && <span className="h-2 w-2 rounded-full bg-red-600 shrink-0" />}
                  </div>
                  <p className="text-sm font-bold text-white mt-1.5 break-words">{u.title}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5 break-words">{u.summary}{(u.body || '').length > 160 ? '…' : ''}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-zinc-600">
                      Effective {u.effective_date} · {new Date(u.created_at).toLocaleString()}
                    </p>
                    <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider" style={{ color: GOLD }}>
                      {u.read ? <><CheckCircle2 className="h-3 w-3" /> Read</> : 'Read Update →'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )
      )}
    </div>
  );
}
